<script>
import { canEditAttendanceMoneyFields } from "@/helpers/userPermissions"
import { resolveCodeTaxId } from "@/helpers/attendance-code-tax"
import { FormWizard, TabContent } from "vue-form-wizard"
import Multiselect from "vue-multiselect"

/**
 * Wizard para inclusão de múltiplas agendas em uma única tela (Issue #309).
 * Consome o endpoint POST /api/atendimento/lote.
 */
export default {
  props: {
    currentUser: { type: Object, required: true },
    currentEmpresa: { type: Object, required: true },
    listAgreements: { type: Array },
    listApproachs: { type: Array },
    listCodeTax: { type: Array },
    listPacients: { type: Array },
    listPrices: { type: Array },
    listProfessionals: { type: Array },
    listSpecialties: { type: Array },
    listTiposPagamento: { type: Array },
  },
  components: { Multiselect, FormWizard, TabContent },
  data() {
    return {
      searchPacient: "",
      searchAgreement: "",
      pacients: [],
      professionals: [],
      specialties: [],
      agreements: [],
      codesTax: [],
      approachs: [],
      tiposPagamento: [],
      service: {
        pacient_id: null,
        pacient: null,
        agreement_id: null,
        agreement: null,
      },
      observacao: "",
      selectedTipoPagamento: null,
      local: "",
      recurrence: "WEEKLY",
      service_value: "",
      transfer_amount: "",
      coparticipation_amount: "",
      agendas: [
        {
          data: "",
          horario_inicio: "",
          horario_fim: "",
          profissional: null,
          tratamento: null,
          treatment_id: null,
          code_tax_id: null,
          approach_id: null,
          touched: false,
        },
      ],
      agendaValidationError: "",
    }
  },
  created() {
    this.pacients = this.listPacients || []
    this.professionals = this.listProfessionals || []
    this.specialties = this.listSpecialties || []
    this.agreements = this.listAgreements || []
    this.codesTax = this.listCodeTax || []
    this.approachs = this.listApproachs || []
    this.tiposPagamento = this.listTiposPagamento || []
  },
  computed: {
    permission() {
      return canEditAttendanceMoneyFields(this.currentUser)
    },
    isPrivate() {
      return this.service.agreement ? this.service.agreement.is_private : false
    },
    totalAgendas() {
      return this.agendas.length
    },
    validAgendasCount() {
      return this.agendas.filter((line, index) => Object.keys(this.getAgendaErrors(line, index)).length === 0)
        .length
    },
  },
  mounted() {
    this.$emit("newTitle", "Incluir multi-agendas")
  },
  methods: {
    back() {
      this.$emit("back")
    },
    filterPacient(nome) {
      if (!nome) {
        this.pacients = this.listPacients || []
      } else {
        this.pacients = this.listPacients.filter((p) =>
          p.nome.toLowerCase().includes(nome.toLowerCase())
        )
      }
    },
    filterAgreements(name) {
      const base =
        this.service.pacient && (this.service.pacient.agreements || []).length > 0
          ? (this.listAgreements || []).filter((a) =>
              this.service.pacient.agreements.some((pa) => pa.id === a.id)
            )
          : (this.listAgreements || [])
      if (!name) {
        this.agreements = base
      } else {
        this.agreements = base.filter((a) =>
          (a.name || "").toLowerCase().includes(name.toLowerCase())
        )
      }
    },
    setPacient(pacient) {
      if (pacient === this.service.pacient) {
        this.service.pacient_id = null
        this.service.pacient = null
        this.searchPacient = ""
        this.agreements = this.listAgreements || []
        return
      }
      this.service.pacient_id = pacient ? pacient.id : null
      this.service.pacient = pacient || null
      if (pacient) {
        this.searchPacient = pacient.nome
        const pacAgreements = pacient.agreements || []
        const listInPacient =
          pacAgreements.length > 0
            ? (this.listAgreements || []).filter((a) =>
                pacAgreements.some((pa) => pa.id === a.id)
              )
            : (this.listAgreements || [])
        this.agreements = listInPacient
        this.$refs.wizard.nextTab()
      } else {
        this.searchPacient = ""
        this.agreements = this.listAgreements || []
      }
    },
    setAgreement(agreement) {
      if (agreement === this.service.agreement) {
        this.service.agreement_id = null
        this.service.agreement = null
        this.searchAgreement = ""
        return
      }
      this.service.agreement_id = agreement ? agreement.id : null
      this.service.agreement = agreement || null
      this.searchAgreement = agreement ? agreement.name : ""
      if (agreement) {
        this.applyAgreementCodeTaxToAgendas()
        this.setPrice()
        this.$refs.wizard.nextTab()
      }
    },

    applyAgreementCodeTaxToAgendas() {
      this.agendas.forEach((agenda) => {
        agenda.code_tax_id = resolveCodeTaxId({
          agreement: this.service.agreement,
          specialty: agenda.tratamento,
        })
      })
    },

    getProfessionalSpecialties(professional) {
      return professional?.specialty || []
    },
    onAgendaProfessionalSelected(agenda) {
      const specialties = this.getProfessionalSpecialties(agenda.profissional)
      if (!specialties.some((specialty) => specialty.id === agenda.treatment_id)) {
        agenda.tratamento = null
        agenda.treatment_id = null
        agenda.code_tax_id = null
      }
      this.touchAgenda(this.agendas.indexOf(agenda))
    },
    getCodeTaxIdFromSpecialty(specialty) {
      return resolveCodeTaxId({
        agreement: this.service.agreement,
        specialty,
      })
    },
    onAgendaTratamentSelected(agenda, specialty) {
      agenda.tratamento = specialty || null
      agenda.treatment_id = specialty?.id || null
      agenda.code_tax_id = this.getCodeTaxIdFromSpecialty(specialty)
      this.setPrice(agenda)
      this.touchAgenda(this.agendas.indexOf(agenda))
    },
    setPrice(agenda) {
      if (agenda?.treatment_id && this.service?.agreement_id && (this.listPrices || []).length > 0) {
        const price = (this.listPrices || []).filter(
          (item) =>
            item.specialty_id == agenda.treatment_id &&
            item.agreement_id == this.service.agreement_id
        )
        if (price.length > 0) {
          this.service_value = this.formatterFloatBR(price[0].service_value)
          this.transfer_amount = this.formatterFloatBR(price[0].transfer_amount)
        }
      }
    },
    onTipoPagamentoSelecionado(tipo) {
      this.selectedTipoPagamento = tipo || null
    },
    formatValor(e, field) {
      let v = (e.target && e.target.value) || ""
      v = v.replace(/\D/g, "").replace(/(\d)(\d{2})$/, "$1,$2")
      if (v) v = v.replace(/(?=(\d{3})+(\D))\B/g, ".")
      this[field] = v
    },
    getAgendaKey(agenda) {
      if (!agenda) return ""
      return [agenda.profissional?.id || "", agenda.data || "", agenda.horario_inicio || "", agenda.horario_fim || ""].join("|")
    },
    isDuplicateAgenda(index) {
      const current = this.agendas[index]
      const key = this.getAgendaKey(current)
      if (!key || key === "|||") return false
      return this.agendas.some((agenda, idx) => idx !== index && this.getAgendaKey(agenda) === key)
    },
    getAgendaErrors(agenda, index) {
      const errors = {}
      if (!agenda.profissional) errors.profissional = "Selecione um profissional"
      if (!agenda.treatment_id) errors.treatment_id = "Selecione um tratamento"
      if (!agenda.code_tax_id) errors.code_tax_id = "Selecione o código fiscal"
      if (!agenda.approach_id) errors.approach_id = "Selecione a abordagem"
      if (!agenda.data) errors.data = "Informe a data"
      if (!agenda.horario_inicio) errors.horario_inicio = "Informe a hora de início"
      if (!agenda.horario_fim) errors.horario_fim = "Informe a hora de término"
      if (agenda.horario_inicio && agenda.horario_fim && agenda.horario_fim <= agenda.horario_inicio) {
        errors.horario_fim = "Hora de término deve ser maior que a hora de início"
      }
      if (
        agenda.profissional &&
        agenda.data &&
        agenda.horario_inicio &&
        agenda.horario_fim &&
        this.isDuplicateAgenda(index)
      ) {
        errors.duplicate = "Linha duplicada na submissão (profissional + data + horário)"
      }
      return errors
    },
    touchAgenda(index) {
      if (this.agendas[index]) this.agendas[index].touched = true
    },
    addAgenda() {
      this.agendas.push({
        data: "",
        horario_inicio: "",
        horario_fim: "",
        profissional: null,
        tratamento: null,
        treatment_id: null,
        code_tax_id: null,
        approach_id: null,
        touched: false,
      })
    },
    removeAgenda(index) {
      if (this.agendas.length <= 1) return
      this.agendas.splice(index, 1)
    },
    validateAgendas() {
      this.agendaValidationError = ""
      this.agendas.forEach((agenda) => {
        agenda.touched = true
      })
      const hasErrors = this.agendas.some((agenda, index) => Object.keys(this.getAgendaErrors(agenda, index)).length > 0)
      if (hasErrors) {
        this.agendaValidationError = "Revise os campos destacados antes de gravar."
        return false
      }
      return true
    },
    parseValor(str) {
      if (!str) return null
      const n = parseFloat(String(str).replace(/\./g, "").replace(",", "."))
      return isNaN(n) ? null : n
    },
    setPost() {
      if (!this.validateAgendas()) return

      this.$emit("doPostLote", {
        paciente_id: this.service.pacient_id,
        observacao: this.observacao || null,
        empresa_id: this.currentEmpresa?.id || null,
        agreement_id: this.service.agreement_id || null,
        local: this.local || null,
        recurrence: this.recurrence || null,
        tipo_pagamento_id: this.selectedTipoPagamento ? this.selectedTipoPagamento.id : null,
        service_value: this.new_convert_srting_float(this.service_value),
        transfer_amount: this.new_convert_srting_float(this.transfer_amount),
        coparticipation_amount: this.new_convert_srting_float(this.coparticipation_amount),
        agendas: this.agendas.map((a) => ({
          data: a.data,
          horario_inicio: a.horario_inicio,
          horario_fim: a.horario_fim,
          profissional_id: a.profissional.id,
          treatment_id: a.treatment_id,
          code_tax_id: a.code_tax_id,
          approach_id: a.approach_id,
        })),
      })
    },
  },
}
</script>

<template>
  <div class="row">
    <div class="col-md-12">
      <div class="card">
        <div class="card-body">
          <FormWizard
            ref="wizard"
            color="#556ee6"
            back-button-text="Voltar"
            next-button-text="Avançar"
            finish-button-text="Gravar multi-agendas"
            @on-complete="setPost"
          >
            <tab-content title="Selecionar Paciente" icon="mdi mdi-face-profile">
              <h2>Selecionar Paciente</h2>
              <b-row cols="1">
                <div class="position-relative">
                  <form class="app-search d-none d-lg-block" @submit.prevent>
                    <div class="position-relative">
                      <input
                        v-model="searchPacient"
                        name="pacient"
                        type="text"
                        class="form-control"
                        placeholder="Buscar por nome do Paciente"
                        @input="filterPacient($event.target.value)"
                      />
                      <span class="ri-search-line"></span>
                    </div>
                  </form>
                </div>
              </b-row>
              <div class="row">
                <div class="col-12 max-h-96 overflow-y-auto">
                  <b-row cols="1" v-for="pacient in pacients" :key="pacient.id" class="cursor-pointer">
                    <b-col @click="setPacient(pacient)">
                      <b-card class="flex" :class="(service.pacient_id === pacient.id) ? 'bg-light' : 'bg-default'">
                        <b-badge variant="primary" class="w-12 h-12 rounded-full items-center justify-center pt-8">
                          <span class="text-lg font-semibold">{{ pacient.nome ? pacient.nome.substring(0, 1) : '' }}</span>
                        </b-badge>
                        <span class="flex items-center ml-3">
                          {{ pacient.nome }} - <i class="ri-phone-line"></i> {{ (pacient.contact) ? pacient.contact : '' }} | <i class="ri-mail-line"></i> {{ pacient.responsible ? pacient.responsible.email : '' }}
                        </span>
                      </b-card>
                    </b-col>
                  </b-row>
                </div>
              </div>
              <div class="form-group mt-4">
                <label>Observação (opcional)</label>
                <textarea
                  v-model="observacao"
                  class="form-control"
                  rows="2"
                  placeholder="Ex: Pacote mensal de terapias"
                />
              </div>
            </tab-content>
            <tab-content title="Selecionar Convênio" icon="mdi mdi-handshake">
              <h2>Selecionar Convênio</h2>
              <b-row cols="1">
                <div class="position-relative">
                  <form class="app-search d-none d-lg-block" @submit.prevent>
                    <div class="position-relative">
                      <input
                        v-model="searchAgreement"
                        name="agreements"
                        type="text"
                        class="form-control"
                        placeholder="Buscar por nome do Convênio"
                        @input="filterAgreements($event.target.value)"
                      />
                      <span class="ri-search-line"></span>
                    </div>
                  </form>
                </div>
              </b-row>
              <div class="row">
                <div class="col-12 max-h-96 overflow-y-auto">
                  <b-row cols="1" v-for="agreement in agreements" :key="agreement.id" class="cursor-pointer">
                    <b-col @click="setAgreement(agreement)">
                      <b-card class="flex" :class="(service.agreement_id === agreement.id) ? 'bg-light' : 'bg-default'">
                        <b-badge variant="primary" class="w-12 h-12 rounded-full items-center justify-center pt-8">
                          <span class="text-lg font-semibold">{{ agreement.name ? agreement.name.substring(0, 1) : '' }}</span>
                        </b-badge>
                        <span class="flex items-center ml-3">
                          {{ agreement.name }}
                        </span>
                      </b-card>
                    </b-col>
                  </b-row>
                </div>
              </div>
            </tab-content>
            <tab-content title="Agendas" icon="mdi mdi-calendar-plus">
              <b-card bg-variant="light" border-variant="light" title="Resumo da seleção" class="mb-3">
                <div class="card-body py-2">
                  <div class="row">
                    <div class="col-md-4">
                      <p class="text-sm text-slate-500 mb-0">Paciente:</p>
                      <p class="mb-0"><strong>{{ service.pacient ? service.pacient.nome : 'Não selecionado' }}</strong></p>
                    </div>
                    <div class="col-md-4" v-if="service.agreement">
                      <p class="text-sm text-slate-500 mb-0">Convênio:</p>
                      <p class="mb-0"><strong>{{ service.agreement.name }}</strong></p>
                    </div>
                  </div>
                </div>
              </b-card>
              <div class="card mb-3">
                <div class="card-body">
                  <div class="row">
                    <div class="form-group col-md-6">
                      <label for="localAtendimento">Local do atendimento:</label>
                      <input
                        id="localAtendimento"
                        v-model="local"
                        type="text"
                        class="form-control"
                        placeholder="Local de atendimento"
                      />
                    </div>
                    <div class="form-group col-md-6">
                      <b-form-group label="Tipo de recorrência:" v-slot="{ ariaDescribedby }">
                        <b-form-radio v-model="recurrence" :aria-describedby="ariaDescribedby" name="recurrence" value="none">Nunca</b-form-radio>
                        <b-form-radio v-model="recurrence" :aria-describedby="ariaDescribedby" name="recurrence" value="WEEKLY">Semanal</b-form-radio>
                      </b-form-group>
                    </div>
                  </div>
                </div>
              </div>
              <div class="d-flex justify-content-between align-items-start flex-wrap mb-3">
                <div>
                  <h2 class="mb-1">Agendas em lote</h2>
                  <p class="text-muted mb-0">Cada linha representa uma agenda com dados completos do atendimento.</p>
                </div>
                <div class="alert alert-info py-2 px-3 mb-0 mt-2 mt-md-0">
                  <div><strong>{{ totalAgendas }}</strong> linha(s)</div>
                  <div>Previsão: <strong>{{ validAgendasCount }}</strong> atendimento(s) / agenda(s)</div>
                </div>
              </div>

              <div v-if="agendaValidationError" class="alert alert-warning py-2">
                {{ agendaValidationError }}
              </div>

              <div v-for="(agenda, index) in agendas" :key="index" class="border rounded p-3 mb-3 bg-light">
                <div class="d-flex justify-content-between align-items-center mb-2">
                  <strong>Linha {{ index + 1 }}</strong>
                  <button
                    v-if="agendas.length > 1"
                    type="button"
                    class="btn btn-sm btn-outline-danger"
                    @click="removeAgenda(index)"
                  >
                    <i class="fas fa-trash"></i> Remover
                  </button>
                </div>
                <div class="row">
                  <div class="form-group col-md-3">
                    <label>Profissional</label>
                    <Multiselect
                      v-model="agenda.profissional"
                      :options="listProfessionals"
                      label="name"
                      track-by="id"
                      :searchable="true"
                      placeholder="Selecione o profissional"
                      :class="{ 'is-invalid-field': agenda.touched && getAgendaErrors(agenda, index).profissional }"
                      @input="onAgendaProfessionalSelected(agenda)"
                    />
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).profissional" class="text-danger">
                      {{ getAgendaErrors(agenda, index).profissional }}
                    </small>
                  </div>
                  <div class="form-group col-md-3">
                    <label>Tratamento</label>
                    <Multiselect
                      v-model="agenda.tratamento"
                      :options="getProfessionalSpecialties(agenda.profissional)"
                      label="name"
                      track-by="id"
                      :searchable="true"
                      placeholder="Selecione o tratamento"
                      :class="{ 'is-invalid-field': agenda.touched && getAgendaErrors(agenda, index).treatment_id }"
                      @input="onAgendaTratamentSelected(agenda, $event)"
                    >
                      <template slot="singleLabel" slot-scope="props">
                        <strong v-if="props.option">{{ props.option.id }}</strong><span v-if="props.option"> - {{ props.option.name }}</span>
                      </template>
                      <template slot="option" slot-scope="props">
                        <strong>{{ props.option.id }}</strong> - {{ props.option.name }}
                      </template>
                    </Multiselect>
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).treatment_id" class="text-danger">
                      {{ getAgendaErrors(agenda, index).treatment_id }}
                    </small>
                  </div>
                  <div class="form-group col-md-2">
                    <label>Código fiscal</label>
                    <select
                      v-model="agenda.code_tax_id"
                      class="form-control"
                      :class="{ 'is-invalid': agenda.touched && getAgendaErrors(agenda, index).code_tax_id }"
                      @change="touchAgenda(index)"
                    >
                      <option :value="null">Selecione o código</option>
                      <option
                        v-for="codetax in codesTax"
                        :key="codetax.id"
                        :value="codetax.id"
                      >
                        {{ codetax.id }} - {{ codetax.name }}
                      </option>
                    </select>
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).code_tax_id" class="text-danger">
                      {{ getAgendaErrors(agenda, index).code_tax_id }}
                    </small>
                  </div>
                  <div class="form-group col-md-2">
                    <label>Abordagem</label>
                    <select
                      v-model="agenda.approach_id"
                      class="form-control"
                      :class="{ 'is-invalid': agenda.touched && getAgendaErrors(agenda, index).approach_id }"
                      @change="touchAgenda(index)"
                    >
                      <option :value="null">Selecione a abordagem</option>
                      <option v-for="a in approachs" :key="a.id" :value="a.id">
                        {{ a.id }} - {{ a.name }}
                      </option>
                    </select>
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).approach_id" class="text-danger">
                      {{ getAgendaErrors(agenda, index).approach_id }}
                    </small>
                  </div>
                  <div class="form-group col-md-2">
                    <label>Data</label>
                    <input
                      v-model="agenda.data"
                      type="date"
                      class="form-control"
                      :class="{ 'is-invalid': agenda.touched && getAgendaErrors(agenda, index).data }"
                      @change="touchAgenda(index)"
                    />
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).data" class="text-danger">
                      {{ getAgendaErrors(agenda, index).data }}
                    </small>
                  </div>
                  <div class="form-group col-md-1">
                    <label>Hora início</label>
                    <input
                      v-model="agenda.horario_inicio"
                      type="time"
                      class="form-control"
                      :class="{ 'is-invalid': agenda.touched && getAgendaErrors(agenda, index).horario_inicio }"
                      @change="touchAgenda(index)"
                    />
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).horario_inicio" class="text-danger">
                      {{ getAgendaErrors(agenda, index).horario_inicio }}
                    </small>
                  </div>
                  <div class="form-group col-md-1">
                    <label>Hora fim</label>
                    <input
                      v-model="agenda.horario_fim"
                      type="time"
                      class="form-control"
                      :class="{ 'is-invalid': agenda.touched && (getAgendaErrors(agenda, index).horario_fim || getAgendaErrors(agenda, index).duplicate) }"
                      @change="touchAgenda(index)"
                    />
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).horario_fim" class="text-danger">
                      {{ getAgendaErrors(agenda, index).horario_fim }}
                    </small>
                    <small v-if="agenda.touched && getAgendaErrors(agenda, index).duplicate" class="text-danger d-block">
                      {{ getAgendaErrors(agenda, index).duplicate }}
                    </small>
                  </div>
                </div>
              </div>
              <button type="button" class="btn btn-outline-primary" @click="addAgenda">
                <i class="fas fa-plus"></i> Adicionar linha
              </button>
            </tab-content>
            <tab-content title="Dados financeiros" icon="mdi mdi-currency-brl">
              <h2>Valores (aplicados a todas as agendas)</h2>
              <div class="row mt-3">
                <div class="form-group col-md-2" v-show="permission || isPrivate">
                  <label>Valor do atendimento</label>
                  <input
                    v-model="service_value"
                    type="text"
                    class="form-control text-right"
                    placeholder="0,00"
                    @input="formatValor($event, 'service_value')"
                  />
                </div>
                <div class="form-group col-md-2" v-show="!isPrivate && permission">
                  <label>Valor coparticipação</label>
                  <input
                    v-model="coparticipation_amount"
                    type="text"
                    class="form-control text-right"
                    placeholder="0,00"
                    @input="formatValor($event, 'coparticipation_amount')"
                  />
                </div>
                <div class="form-group col-md-2" v-show="permission">
                  <label>Valor do repasse</label>
                  <input
                    v-model="transfer_amount"
                    type="text"
                    class="form-control text-right"
                    placeholder="0,00"
                    @input="formatValor($event, 'transfer_amount')"
                  />
                </div>
                <div class="form-group col-md-8">
                  <label>Tipo de pagamento</label>
                  <Multiselect
                    v-model="selectedTipoPagamento"
                    :options="tiposPagamento"
                    label="tiponome"
                    track-by="id"
                    placeholder="Selecione"
                    @input="onTipoPagamentoSelecionado"
                  >
                    <template slot="singleLabel" slot-scope="props">
                      {{ props.option ? props.option.tiponome : '' }}
                    </template>
                  </Multiselect>
                </div>
              </div>
            </tab-content>
          </FormWizard>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.max-h-96 {
  max-height: 24rem;
}
.overflow-y-auto {
  overflow-y: auto;
}
.cursor-pointer {
  cursor: pointer;
}
.is-invalid-field {
  border: 1px solid #f46a6a;
  border-radius: 0.25rem;
}
</style>
